import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Wrench, XCircle } from "lucide-react";

// Reparación ACOTADA de objetos de Storage que fallaron al subir durante el import real
// (cuenta NUEVA). Solo usa endpoints que ya existen y siempre con X-Migration-Token:
//   POST /admin/migration/repair-storage  (+ GET .../status)
//   POST /admin/migration/verify-storage  (+ GET .../status)
// No importa, no vacía, no toca el token ni la base (salvo el storage_index de esas rutas, como el
// propio import). Las rutas salen SOLO de import_job.result.storage_errors (las exactas que dejó el
// servidor); nunca se tipean ni se reconstruyen. Nunca se muestra ni se guarda la URL ni el token.

const ONLY_UPLOAD_FAILURES = /^Storage: \d+ objeto\(s\) no se pudieron subir$/;
const POLL_MS = 2500;
const POLL_MAX = 480; // ~20 min por job

// Devuelve { ok, reason, paths }. ok=true solo si el import REAL terminó con errores y TODOS los
// problemas son "objetos de Storage que no se pudieron subir", con la lista de rutas completa.
export function repairPlan(importJobStatus, importResult) {
  const no = (reason) => ({ ok: false, reason, paths: [] });
  if (!importJobStatus || importJobStatus.status !== "error" || importJobStatus.dry_run !== false) return no("no_real_import_error");
  const r = importResult;
  if (!r || r.aborted_before_write) return no("no_result");
  const problems = Array.isArray(r.problems) ? r.problems : [];
  if (problems.length === 0 || !problems.every((p) => ONLY_UPLOAD_FAILURES.test(String(p)))) return no("other_problems");
  const st = r.imported?.storage_objects || {};
  if (st.checksum_mismatch) return no("checksum_mismatch");
  const errs = Array.isArray(r.storage_errors) ? r.storage_errors : [];
  const paths = errs.map((e) => (e && typeof e.path === "string" ? e.path : ""));
  if (paths.some((p) => !p || p !== p.trim())) return no("bad_path");
  if (new Set(paths).size !== paths.length) return no("duplicate_paths");
  // Lista completa: el servidor la recorta (STORAGE_ERRORS_MAX); si hay más fallidos que rutas, no se ofrece.
  if (!(st.failed > 0) || r.storage_errors_total !== st.failed || paths.length !== st.failed) return no("incomplete_list");
  return { ok: true, reason: null, paths };
}

// "Reparado" exige: job done, fallidos vacío y que el job haya revisado EXACTAMENTE las rutas pedidas
// (si una ruta no está en el ZIP, el backend no avisa: simplemente no la revisa).
export function repairVerdict(plan, s) {
  if (!s || s.status === "running") return { state: "running" };
  if (s.status === "error") return { state: "error", message: s.error || "Falló el job de reparación" };
  if (s.status !== "done") return { state: "error", message: "Estado inesperado del job de reparación" };
  const res = s.result;
  if (!res || !Array.isArray(res.fallidos)) return { state: "error", message: "El job terminó sin detalle de resultado" };
  if (res.fallidos.length > 0) return { state: "failed", message: `${res.fallidos.length} objeto(s) no se pudieron subir`, failed: res.fallidos };
  const pend = (res.sin_prueba?.length || 0) + (res.faltan_en_zip?.length || 0);
  if (pend > 0) return { state: "mismatch", message: `${pend} objeto(s) sin prueba de tamaño/SHA256 o ausentes del export. No cuenta como reparado.` };
  const n = plan.paths.length;
  if (res.revisados !== n || (res.subidos || 0) + (res.ya_estaban || 0) !== n) {
    return { state: "mismatch", message: `Se pidieron ${n} objeto(s) y el job revisó ${res.revisados} (subidos ${res.subidos || 0}, ya estaban ${res.ya_estaban || 0}). No cuenta como reparado.` };
  }
  return { state: "ok", subidos: res.subidos || 0, ya_estaban: res.ya_estaban || 0 };
}

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));
// Todo texto que viene del servidor se muestra redactado: nunca una URL firmada (exp/sig) en pantalla.
const redact = (t) => String(t ?? "").replace(/\b(sig|exp|token|key|secret|signature)=[^&\s"'<>]+/gi, "$1=***");
const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return redact(d);
  if (d && typeof d === "object" && d.message) return redact(d.message);
  return fallback;
};

export default function MigrationRepairStorage({ token, sourceUrl = "", importJobStatus, importResult, onActionError, onVerifyTerminal, pollMs = POLL_MS }) {
  const plan = repairPlan(importJobStatus, importResult);
  const [confirm, setConfirm] = useState("");
  const [url, setUrl] = useState("");            // URL firmada del export; campo oculto (type=password), nunca se muestra
  const effUrl = (url || sourceUrl || "").trim();
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);          // último estado del job de reparación
  const [vBusy, setVBusy] = useState(false);
  const [vJob, setVJob] = useState(null);        // último estado del job de verificación
  const [vMsg, setVMsg] = useState(null);
  const aliveRef = useRef(true);
  const hdr = () => ({ headers: { "X-Migration-Token": token } });
  const optHdr = () => (token ? hdr() : undefined);

  // Espera el job `jid` (o cualquiera, si jid es null) hasta que termine. Ignora el estado del job anterior.
  const waitJob = async (path, jid, onTick) => {
    for (let i = 0; i < POLL_MAX && aliveRef.current; i++) {
      await sleep(pollMs);
      if (!aliveRef.current) return null;
      let sd;
      try {
        sd = (await api.get(path, optHdr())).data || {};
      } catch (e) {
        if (e?.response?.status === 401 || e?.response?.status === 403) throw e;
        continue; // transitorio
      }
      if (jid && sd.job_id && sd.job_id !== jid) continue;
      if (onTick) onTick(sd);
      if (sd.status === "done" || sd.status === "error") return sd;
    }
    return aliveRef.current ? { status: "error", error: "Se agotó la espera del job. Revisa su estado antes de repetirlo." } : null;
  };

  useEffect(() => {
    aliveRef.current = true;
    if (!plan.ok) return () => { aliveRef.current = false; };
    // Al montar: mostrar el último job y, si sigue corriendo, retomarlo (evita lanzar un segundo job).
    (async () => {
      try {
        const sd = (await api.get("/admin/migration/repair-storage/status", optHdr())).data || {};
        if (!aliveRef.current || !sd.status || sd.status === "never_run") return;
        setJob(sd);
        if (sd.status === "running") {
          setBusy(true);
          const fin = await waitJob("/admin/migration/repair-storage/status", sd.job_id || null, setJob);
          if (fin && aliveRef.current) setJob(fin);
          if (aliveRef.current) setBusy(false);
        }
      } catch (e) { /* noop: es solo informativo */ }
    })();
    return () => { aliveRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan.ok]);

  if (!plan.ok) return null;

  const verdict = job ? repairVerdict(plan, job) : null;
  const repaired = verdict?.state === "ok";
  const canStart = !busy && !repaired && !!token && !!effUrl && confirm.trim().toUpperCase() === "REPARAR" && plan.paths.length > 0;

  const start = async () => {
    if (!plan.ok || plan.paths.length === 0) return;
    if (!token) { toast.error("Ingresa el X-Migration-Token arriba"); return; }
    if (!effUrl) { toast.error("Pega la URL de descarga del export (la misma del import)"); return; }
    if (confirm.trim().toUpperCase() !== "REPARAR") return;
    setBusy(true);
    try {
      const cur = (await api.get("/admin/migration/repair-storage/status", hdr())).data || {};
      if (cur.status === "running") { toast.error("Ya hay un job de reparación en curso"); setJob(cur); return; }
      setJob({ status: "running", step: "iniciando" });
      const st = await api.post("/admin/migration/repair-storage", { source_url: effUrl, paths: [...plan.paths] }, hdr());
      const fin = await waitJob("/admin/migration/repair-storage/status", st.data?.job_id || null, setJob);
      if (fin && aliveRef.current) {
        setJob(fin);
        const v = repairVerdict(plan, fin);
        if (v.state === "ok") toast.success("Objetos reparados — falta verificarlos");
        else toast.error(redact(v.message) || "La reparación no quedó completa");
      }
    } catch (e) {
      setJob(null);
      if (onActionError) onActionError(e, "Error al reparar los objetos de Storage");
      else toast.error(errText(e, "Error al reparar los objetos de Storage"));
    } finally { if (aliveRef.current) setBusy(false); }
  };

  const verify = async () => {
    if (!token) { toast.error("Ingresa el X-Migration-Token arriba"); return; }
    setVBusy(true); setVMsg(null); setVJob(null);
    try {
      let jid = null;
      try {
        jid = (await api.post("/admin/migration/verify-storage", {}, hdr())).data?.job_id || null;
      } catch (e) {
        const d = e?.response?.data?.detail;
        if (e?.response?.status === 409 && d?.code === "no_clean_import") {
          setVMsg("El servidor no permite verificar mientras el import figure con errores (no_clean_import). Hace falta el ajuste de backend que lo habilita tras reparar.");
          return;
        }
        if (!(e?.response?.status === 409 && d?.code === "verify_running")) throw e;
      }
      const fin = await waitJob("/admin/migration/verify-storage/status", jid, setVJob);
      if (fin && aliveRef.current) setVJob(fin);
      // Terminal: relee (solo GET) el estado del import para recoger la acreditación derivada del backend.
      if (fin && aliveRef.current && onVerifyTerminal) await onVerifyTerminal();
    } catch (e) {
      if (onActionError) onActionError(e, "Error al verificar los objetos de Storage");
      else toast.error(errText(e, "Error al verificar los objetos de Storage"));
    } finally { if (aliveRef.current) setVBusy(false); }
  };

  const vRes = vJob?.status === "done" ? vJob.result : null;
  const vCounts = vRes?.counts || {};

  return (
    <div className="rounded-2xl border border-amber-300 bg-amber-50 px-6 py-4 text-sm text-amber-900 space-y-3" data-testid="migration-repair-storage">
      <div className="font-semibold flex items-center gap-1.5"><Wrench className="w-4 h-4" /> Reparar objetos de Storage que fallaron en el import</div>
      <p className="text-xs">
        El import terminó con errores solo por {plan.paths.length} objeto(s) de Storage que no se pudieron subir. Esto vuelve a subir
        exactamente estos objeto(s) desde el mismo export (los que ya estén, se saltean). No importa de nuevo, no vacía la cuenta y no toca el token.
      </p>
      <div className="font-mono text-xs break-all space-y-0.5" data-testid="migration-repair-paths">
        {plan.paths.map((p) => <div key={p}>• {p}</div>)}
      </div>
      <div>
        <label className="text-xs font-semibold">URL de descarga del export (la misma del import; queda oculta) {sourceUrl.trim() ? "— ya cargada desde el paso 2" : ""}</label>
        <input data-testid="migration-repair-url-input" type="password" autoComplete="off" value={url} onChange={(e) => setUrl(e.target.value)} disabled={busy}
          className="w-full mt-1 px-3 py-2 rounded-lg border border-amber-300 text-sm font-mono" placeholder="Pega la URL firmada del export" />
      </div>
      <p className="text-xs">Usa además el X-Migration-Token de esta sesión (campo de arriba).</p>
      <div className="flex gap-2">
        <input data-testid="migration-repair-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={busy}
          className="flex-1 px-3 py-2 rounded-lg border border-amber-300 text-sm font-mono" placeholder="REPARAR" />
        <button data-testid="migration-repair-btn" onClick={start} disabled={!canStart}
          className="px-4 py-2 rounded-xl text-sm font-bold bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          {busy ? "Reparando..." : `Reparar ${plan.paths.length} objeto(s)`}
        </button>
      </div>

      {job && (
        <div className="text-xs space-y-1" data-testid="migration-repair-result">
          {verdict?.state === "running" && <div className="flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {job.step ? `Reparando: ${job.step}` : "Reparando…"}</div>}
          {verdict?.state === "ok" && (
            <div className="font-semibold text-emerald-800 flex items-center gap-1.5" data-testid="migration-repair-ok">
              <CheckCircle2 className="w-4 h-4" /> Reparado: subidos {verdict.subidos}, ya estaban {verdict.ya_estaban}, fallidos 0. Falta verificar.
            </div>
          )}
          {(verdict?.state === "error" || verdict?.state === "mismatch" || verdict?.state === "failed") && (
            <div className="font-semibold text-red-800 flex items-center gap-1.5" data-testid="migration-repair-error"><XCircle className="w-4 h-4" /> {redact(verdict.message)}</div>
          )}
          {(verdict?.failed || []).map((f, i) => <div key={i} className="font-mono break-all text-red-800">• {f.path}: {redact(f.error)}</div>)}
        </div>
      )}

      {repaired && (
        <div className="space-y-2 pt-1 border-t border-amber-200">
          <button data-testid="migration-repair-verify-btn" onClick={verify} disabled={vBusy}
            className="px-4 py-2 rounded-xl text-sm font-bold bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-40 inline-flex items-center gap-2">
            {vBusy && <Loader2 className="w-4 h-4 animate-spin" />}
            Verificar objetos de Storage (existencia, tamaño y SHA256)
          </button>
          {vMsg && <div className="text-xs font-semibold text-red-800" data-testid="migration-repair-verify-msg">{vMsg}</div>}
          {vJob?.status === "error" && <div className="text-xs font-semibold text-red-800" data-testid="migration-repair-verify-error">{redact(vJob.error) || "Falló la verificación"}</div>}
          {vRes && (
            <div className="text-xs space-y-1" data-testid="migration-repair-verify-result">
              <div className={`font-semibold ${vRes.ok ? "text-emerald-800" : "text-red-800"}`}>
                {vRes.ok ? "Verificado: todos los objetos existen con el tamaño y SHA256 correctos." : "La verificación encontró diferencias."}
              </div>
              <div>Índice: {vCounts.index_docs} · ok {vCounts.ok} · faltan {vCounts.missing} · distinto {vCounts.mismatch} · sin leer {vCounts.unreadable} · ya faltaban en el export {vCounts.known_missing} · sin SHA {vCounts.without_sha}</div>
              {["missing", "mismatch", "unreadable"].flatMap((k) => (vRes[k] || []).slice(0, 10).map((it, i) => (
                <div key={`${k}${i}`} className="font-mono break-all text-red-800">• {k}: {it.path}{it.fields ? ` (${it.fields.join(", ")})` : ""}</div>
              )))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
